import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

type Query = ReturnType<ReturnType<typeof supabase.from>["select"]>;

interface Options {
  /** Extra filters, e.g. q => q.eq("page", "contact") */
  refine?: (q: Query) => Query;
  /** Column used for the primary ordering (default sort_order). */
  orderBy?: string;
}

export interface PublicRowsResult<T> {
  rows: T[];
  loading: boolean;
  /** True when rows came from the database (as opposed to the fallback). */
  fromDb: boolean;
}

/**
 * Load public content rows (`is_active = true`) from a Supabase table.
 *
 * When the request fails — typically because the table does not exist yet
 * (database not migrated) or the network is unavailable — the provided
 * `fallback` is used so the page keeps rendering the built-in content.
 * An empty successful result is respected: it means the association has
 * intentionally not published anything for that section.
 */
export function usePublicRows<T>(table: string, fallback: T[], options: Options = {}): PublicRowsResult<T> {
  const [state, setState] = useState<PublicRowsResult<T>>({ rows: fallback, loading: true, fromDb: false });
  const { refine, orderBy = "sort_order" } = options;

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        let query = supabase.from(table).select("*").eq("is_active", true) as Query;
        if (refine) query = refine(query);
        const { data, error } = await query.order(orderBy, { ascending: true }).order("id", { ascending: true });
        if (cancelled) return;
        if (error || !data) {
          setState({ rows: fallback, loading: false, fromDb: false });
        } else {
          setState({ rows: data as T[], loading: false, fromDb: true });
        }
      } catch {
        if (!cancelled) setState({ rows: fallback, loading: false, fromDb: false });
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
    // `fallback` and `refine` are treated as stable module-level values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, orderBy]);

  return state;
}
